import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import styles from './DetailsCard.module.scss'
import { Col, Container, Row } from 'react-grid-system'
import { Button, Heading, Paragraph } from '~elements'
import { DetailsCardProps } from '~types'
import { useWindowSize, useIsSmartphoneLandscape } from 'utils/hooks'
import { getGtmLinkPosition } from '~analyticsUtils'
import { isMobileOnly } from 'react-device-detect'

type DetailsCardTextContentType = Pick<DetailsCardProps, 'description' | 'headline' | 'eyebrow'>

const DetailsCard = ({
  description,
  headline,
  eyebrow,
  cta,
  isDescriptionVisibleDesktopOrTablet,
  isDescriptionVisibleMobile,
  eyebrowColor,
  setDetailsCardHeight,
  tileIndex
}: DetailsCardProps) => {
  const observedElementRef = useRef<HTMLDivElement>(null)
  // Removed mounting animation due to choppiness
  // const [mountedUnmounted, setMountedUnmounted] = useState('')
  const [cardTextContent, setCardTextContent] = useState<DetailsCardTextContentType>({
    description,
    headline,
    eyebrow
  })
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const { isBreakpoint: isBelowBreakpointLg } = useWindowSize('lg', '<')
  const isSmartphoneLandscape = useIsSmartphoneLandscape() || isMobileOnly
  const paragraphFontSize = isSmartphoneLandscape || isSmartphoneLandscape ? 'md' : 'lg'

  useEffect(() => {
    if (observedElementRef.current) {
      const observer = new ResizeObserver(entries => {
        try {
          let bannerHeight = 0
          let disclaimerContainerHeight = 0
          const getDetailsCardHeightFromRef = () =>
            observedElementRef && observedElementRef.current
              ? observedElementRef.current.getBoundingClientRect().height
              : 0

          const detailsCardElement = entries[0]
          const detailsCardHeight = detailsCardElement.contentRect.height || getDetailsCardHeightFromRef()

          if (entries.length > 1) {
            const bannerElement = entries.find(x => x.target.id === 'notification-banner')!
            const discElement = entries.find(x => x.target.id === 'disclaimer-container')!

            if (bannerElement) {
              const bannerDimension = bannerElement.target.getBoundingClientRect()
              bannerHeight = bannerDimension.height
            }

            if (discElement) {
              const discDimension = discElement.target.getBoundingClientRect()
              disclaimerContainerHeight = discDimension.height
            }
          } else {
            const element = document.getElementById('notification-banner')

            if (element) {
              const bannerDimensionsRect = element.getBoundingClientRect()
              bannerHeight = bannerDimensionsRect.height
            }

            const discElement = document.getElementById('disclaimer-container')

            if (discElement) {
              const discDimensionsRect = discElement.getBoundingClientRect()
              disclaimerContainerHeight = discDimensionsRect.height
            }
          }

          const totalHeight =
            Math.round(detailsCardHeight) + Math.round(bannerHeight) + Math.round(disclaimerContainerHeight)

          setDetailsCardHeight(totalHeight)
        } catch (error) {
          console.warn('Unable to fetch dimensions - ', error)
        }
      })

      observer.observe(observedElementRef.current)
      const bannerElement = document.getElementById('notification-banner')

      if (bannerElement) {
        observer.observe(bannerElement)
      }

      const disclaimerContainer = document.getElementById('disclaimer-container')

      if (disclaimerContainer) {
        observer.observe(disclaimerContainer)
      }

      // Cleanup function
      return () => {
        observer.disconnect()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (isBelowBreakpointMd) {
      timeoutId = setTimeout(() => {
        // setMountedUnmounted('mounted')
        setCardTextContent({
          description,
          headline,
          eyebrow
        })
      }, 250)

      // setMountedUnmounted('unmounted')
    } else {
      setCardTextContent({
        description,
        headline,
        eyebrow
      })
    }

    return () => clearTimeout(timeoutId)
    // We should look into fixing this, for now lets ignore it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, headline, eyebrow])

  const getIsDescriptionVisible = () => {
    if (!description || !description.html) return false

    if (isBelowBreakpointMd) {
      return isDescriptionVisibleMobile
    } else {
      return isDescriptionVisibleDesktopOrTablet
    }
  }

  return (
    <Container
      className={cx(styles['container'], styles['parent-container'])}
      data-type-landscape={isSmartphoneLandscape}
    >
      <Row>
        <Col>
          <div className={cx([styles['card-details-container']])} ref={observedElementRef}>
            <div className={styles['card-details']}>
              <div className={styles['headline-container']}>
                <div className={styles['eyebrow']}>
                  {eyebrow && (
                    <Heading
                      level={5}
                      justify={isBelowBreakpointLg ? 'center' : 'left'}
                      color={eyebrowColor}
                      className={'eyebrow'}
                    >
                      {cardTextContent.eyebrow}
                    </Heading>
                  )}
                </div>
                {headline && (
                  <Heading level={1} justify={isBelowBreakpointLg ? 'center' : 'left'}>
                    {cardTextContent.headline}
                  </Heading>
                )}
                {getIsDescriptionVisible() && (
                  <Paragraph
                    fontSize={paragraphFontSize}
                    text={cardTextContent.description}
                    justify={isBelowBreakpointLg ? 'center' : 'left'}
                  />
                )}
                {
                  // @ts-ignore
                  cta && cta.length > 0 && (
                    <div className={styles['btn-container']} data-type-landscape={isSmartphoneLandscape}>
                      {
                        // @ts-ignore
                        cta.map((button, index) => (
                          <Button
                            key={`${button.title}_${index}`}
                            {...button}
                            analytics={{
                              linkModule: 'hp_hero',
                              linkPosition: getGtmLinkPosition(cta, index)
                            }}
                          />
                        ))
                      }
                    </div>
                  )
                }
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

DetailsCard.displayName = 'details_card'

export default DetailsCard
